import React, { useState, useEffect, useCallback } from 'react';
import { isAuthenticated, getCurrentUser } from '../utils/api';
import { useNavigate } from 'react-router-dom';
import './AdminPanel.css';
import { API_URL } from '../utils/networkConfig';
import { IoStatsChartOutline, IoPersonOutline, IoHelpCircleOutline, IoCreateOutline, IoTrashOutline, IoWarningOutline, IoCheckmarkCircleOutline, IoBarChartOutline, IoPeopleOutline, IoChatbubbleEllipsesOutline, IoGameControllerOutline, IoRefreshOutline, IoSearchOutline, IoHomeOutline, IoTvOutline, IoBriefcaseOutline, IoSettingsOutline, IoCalendarOutline, IoDocumentTextOutline, IoTrendingUpOutline, IoStarOutline } from 'react-icons/io5';

function AdminPanel() {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [users, setUsers] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [editingUser, setEditingUser] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [userSearchTerm, setUserSearchTerm] = useState('');
  const [questionSearchTerm, setQuestionSearchTerm] = useState('');
  const navigate = useNavigate();

  const fetchStats = useCallback(async () => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        console.warn('No auth token found for stats');
        return;
      }

      console.log('Fetching stats from:', `${API_URL}/api/game-sessions/admin/stats`);
      const response = await fetch(`${API_URL}/api/game-sessions/admin/stats`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      console.log('Stats response status:', response.status);
      if (response.ok) {
        const data = await response.json();
        console.log('Stats response data:', data);
        
        // The backend always returns { stats: { ... } }
        if (!data.stats) {
          console.error('Invalid stats response format:', data);
          throw new Error('Invalid stats response format');
        }
        
        setStats({
          total_users: data.stats.total_users || 0,
          total_questions: data.stats.total_questions || 0,
          total_game_sessions: data.stats.total_game_sessions || 0,
          total_points: data.stats.total_points || 0,
          user_breakdown: data.stats.user_breakdown || {},
          question_breakdown: data.stats.question_breakdown || {}
        });
      } else {
        console.error('Failed to fetch stats:', await response.text());
        throw new Error(`Failed to fetch stats: ${response.status}`);
      }
    } catch (err) {
      console.error('Fetch stats error:', err);
      setError('Failed to load dashboard statistics. Please try again.');
      setStats({
        total_users: 0,
        total_questions: 0,
        total_game_sessions: 0,
        total_points: 0,
        user_breakdown: {},
        question_breakdown: {}
      });
    }
  }, [setError]);

  const fetchUsers = useCallback(async () => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) return;

      console.log('Fetching users from:', `${API_URL}/api/game-sessions/admin/users`);
      const response = await fetch(`${API_URL}/api/game-sessions/admin/users`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      console.log('Users response status:', response.status);
      const data = await response.json();
      console.log('Users response data:', data);
      
      if (response.ok) {
        // Handle both direct array and object with users property
        const usersArray = Array.isArray(data) ? data : (data.users || []);
        console.log('Setting users:', usersArray);
        setUsers(usersArray);
      } else {
        console.error('Failed to fetch users:', data.error);
      }
    } catch (err) {
      console.error('Fetch users error:', err);
    }
  }, []);

  const fetchQuestions = useCallback(async () => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) return;

      console.log('Fetching questions from:', `${API_URL}/api/game-sessions/admin/questions`);
      const response = await fetch(`${API_URL}/api/game-sessions/admin/questions`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      console.log('Questions response status:', response.status);
      const data = await response.json();
      console.log('Questions response data:', data);
      
      if (response.ok) {
        // Handle both direct array and object with questions property
        const questionsArray = Array.isArray(data) ? data : (data.questions || []);
        console.log('Setting questions:', questionsArray);
        setQuestions(questionsArray);
      } else {
        console.error('Failed to fetch questions:', data.error);
      }
    } catch (err) {
      console.error('Fetch questions error:', err);
    }
  }, []);

  const fetchAdminData = useCallback(async () => {
    setLoading(true);
    try {
      await Promise.all([
        fetchStats(),
        fetchUsers(),
        fetchQuestions()
      ]);
    } catch (error) {
      console.error('Error fetching admin data:', error);
      setError('Failed to load admin data. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [fetchStats, fetchUsers, fetchQuestions]);

  useEffect(() => {
    console.log('AdminPanel useEffect triggered');
    // Check if user is authenticated and is admin
    if (!isAuthenticated()) {
      console.log('User not authenticated, redirecting to login');
      navigate('/login');
      return;
    }
    
    const user = getCurrentUser();
    if (!user || user.role_id !== 1) {
      console.log('User not admin, redirecting to home');
      navigate('/');
      return;
    }

    console.log('Loading admin data...');
    // Load initial data
    fetchAdminData();
  }, [navigate, fetchAdminData]);

  useEffect(() => {
    if (activeTab === 'users' && users.length === 0) {
      fetchUsers();
    } else if (activeTab === 'questions' && questions.length === 0) {
      fetchQuestions();
    }
  }, [activeTab, fetchUsers, fetchQuestions, users.length, questions.length]);

  const handleEditUser = (user) => {
    setEditingUser({
      id: user.id,
      name: user.name,
      email: user.email,
      role_id: user.role_id
    });
  };

  const handleSaveUser = async () => {
    try {
      const token = localStorage.getItem('authToken');
      
      const response = await fetch(`${API_URL}/api/game-sessions/admin/users/${editingUser.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          name: editingUser.name,
          email: editingUser.email,
          role_id: editingUser.role_id
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update user');
      }
      
      const data = await response.json();
      
      // Update users list
      setUsers(users.map(user => 
        user.id === editingUser.id ? data.user : user
      ));
      
      setEditingUser(null);
      setError('');
      setSuccess('User updated successfully!');
    } catch (err) {
      console.error('Error updating user:', err);
      setError(err.message);
    }
  };

  const handleDeleteUser = async (userId) => {
    try {
      const token = localStorage.getItem('authToken');
      
      const response = await fetch(`${API_URL}/api/game-sessions/admin/users/${userId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to delete user');
      }
      
      // Remove user from list
      setUsers(users.filter(user => user.id !== userId));
      setShowDeleteConfirm(null);
      setError('');
      setSuccess('User deleted successfully!');
    } catch (err) {
      console.error('Error deleting user:', err);
      setError(err.message);
    }
  };

  const handleDeleteQuestion = async (questionId) => {
    try {
      const token = localStorage.getItem('authToken');
      
      const response = await fetch(`${API_URL}/api/game-sessions/admin/questions/${questionId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to delete question');
      }
      
      // Remove question from list
      setQuestions(questions.filter(question => question.id !== questionId));
      setShowDeleteConfirm(null);
      setError('');
      setSuccess('Question deleted successfully!');
    } catch (err) {
      console.error('Error deleting question:', err);
      setError(err.message);
    }
  };

  const getRoleLabel = (roleId) => {
    switch (roleId) {
      case 1: return 'Admin';
      case 2: return 'Home Viewer';
      case 3: return 'Contestant';
      default: return 'Unknown';
    }
  };

  const getRoleBadgeClass = (roleId) => {
    switch (roleId) {
      case 1: return 'role-admin';
      case 2: return 'role-home';
      case 3: return 'role-contestant';
      default: return 'role-unknown';
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  // Filter functions for search
  const filteredUsers = users.filter(user => {
    const searchLower = userSearchTerm.toLowerCase();
    return (
      (user.name || '').toLowerCase().includes(searchLower) ||
      (user.email || '').toLowerCase().includes(searchLower) ||
      getRoleLabel(user.role_id).toLowerCase().includes(searchLower) ||
      (user.id || '').toString().includes(searchLower)
    );
  });

  const filteredQuestions = questions.filter(question => {
    const searchLower = questionSearchTerm.toLowerCase();
    return (
      question.question_text.toLowerCase().includes(searchLower) ||
      question.id.toString().includes(searchLower) ||
      (question.session_date && question.session_date.includes(questionSearchTerm))
    );
  });

  const clearUserSearch = () => {
    setUserSearchTerm('');
  };

  const clearQuestionSearch = () => {
    setQuestionSearchTerm('');
  };

  if (loading && activeTab === 'dashboard') {
    return (
      <div className="admin-panel">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading admin dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-panel">
      <div className="admin-header">
        <h1 className="heading-with-icon">
          <IoSettingsOutline className="inline-icon" />
          Admin Panel
        </h1>
        <p>Manage users, questions, and system settings</p>
      </div>

      {error && (
        <div className="error-message">
          <IoWarningOutline className="inline-icon" /> {error}
          <button onClick={() => setError('')} className="error-close">×</button>
        </div>
      )}

      {success && (
        <div className="success-message">
          <IoCheckmarkCircleOutline className="inline-icon" /> {success}
          <button onClick={() => setSuccess('')} className="success-close">×</button>
        </div>
      )}

      <div className="admin-tabs">
        <button 
          className={`admin-tab ${activeTab === 'dashboard' ? 'active' : ''}`}
          onClick={() => setActiveTab('dashboard')}
        >
          <IoBarChartOutline className="inline-icon" /> Dashboard
        </button>
        <button 
          className={`admin-tab ${activeTab === 'users' ? 'active' : ''}`}
          onClick={() => setActiveTab('users')}
        >
          <IoPeopleOutline className="inline-icon" /> Users
        </button>
        <button 
          className={`admin-tab ${activeTab === 'questions' ? 'active' : ''}`}
          onClick={() => setActiveTab('questions')}
        >
          <IoChatbubbleEllipsesOutline className="inline-icon" /> Questions
        </button>
      </div>

      {activeTab === 'dashboard' && (
        <div className="admin-dashboard">
          <div className="dashboard-header">
            <h2>
              <IoBarChartOutline className="inline-icon" />
              Dashboard Overview
            </h2>
          </div>

          <div className="stats-grid">
            <div className="stat-card users-card">
              <div className="stat-header">
                <div className="stat-icon-wrapper">
                  <IoPeopleOutline className="stat-icon" />
                </div>
                <h3 className="stat-title">Total Users</h3>
              </div>
              <div className="stat-content">
                <div className={`stat-value ${!stats.total_users ? 'loading' : ''}`}>
                  {stats.total_users?.toLocaleString() || '0'}
                </div>
                <div className="stat-breakdown">
                  <span>Admins: {stats.user_breakdown?.admins || 0}</span>
                  <span>Home Viewers: {stats.user_breakdown?.home_viewers || 0}</span>
                  <span>Contestants: {stats.user_breakdown?.contestants || 0}</span>
                </div>
              </div>
              <div className="stat-footer">
                <div className="stat-period">Updated today</div>
              </div>
            </div>

            <div className="stat-card questions-card">
              <div className="stat-header">
                <div className="stat-icon-wrapper">
                  <IoChatbubbleEllipsesOutline className="stat-icon" />
                </div>
                <h3 className="stat-title">Total Questions</h3>
              </div>
              <div className="stat-content">
                <div className={`stat-value ${!stats.total_questions ? 'loading' : ''}`}>
                  {stats.total_questions?.toLocaleString() || '0'}
                </div>
                <div className="stat-breakdown">
                  <span>On Air: {stats.question_breakdown?.on_air || 0}</span>
                  <span>To Home: {stats.question_breakdown?.to_home || 0}</span>
                </div>
              </div>
              <div className="stat-footer">
                <div className="stat-period">Updated today</div>
              </div>
            </div>

            <div className="stat-card sessions-card">
              <div className="stat-header">
                <div className="stat-icon-wrapper">
                  <IoGameControllerOutline className="stat-icon" />
                </div>
                <h3 className="stat-title">Game Sessions</h3>
              </div>
              <div className="stat-content">
                <div className={`stat-value ${!stats.total_game_sessions ? 'loading' : ''}`}>
                  {stats.total_game_sessions?.toLocaleString() || '0'}
                </div>
              </div>
              <div className="stat-footer">
                <div className="stat-period">Updated today</div>
              </div>
            </div>

            <div className="stat-card points-card">
              <div className="stat-header">
                <div className="stat-icon-wrapper">
                  <IoStarOutline className="stat-icon" />
                </div>
                <h3 className="stat-title">Total Points</h3>
              </div>
              <div className="stat-content">
                <div className={`stat-value ${!stats.total_points ? 'loading' : ''}`}>
                  {stats.total_points?.toLocaleString() || '0'}
                </div>
              </div>
              <div className="stat-footer">
                <div className="stat-period">Updated today</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'users' && (
        <div className="tab-content">
          <div className="tab-header">
            <h2><IoPersonOutline className="inline-icon" /> Manage Users</h2>
            <div className="search-bar">
              <IoSearchOutline className="inline-icon" />
              <input
                type="text"
                placeholder="Search users..."
                value={userSearchTerm}
                onChange={(e) => setUserSearchTerm(e.target.value)}
              />
              {userSearchTerm && (
                <button onClick={clearUserSearch} className="clear-search">×</button>
              )}
            </div>
          </div>

          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Role</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map(user => (
                  <tr key={user.id}>
                    <td>{user.id}</td>
                    <td>{user.name}</td>
                    <td>{user.email}</td>
                    <td>
                      <span className={`role-badge ${getRoleBadgeClass(user.role_id)}`}>
                        {getRoleLabel(user.role_id)}
                      </span>
                    </td>
                    <td>
                      <div className="action-buttons">
                        <button onClick={() => handleEditUser(user)} className="edit-btn">
                          <IoCreateOutline className="inline-icon" /> Edit
                        </button>
                        <button onClick={() => setShowDeleteConfirm(user.id)} className="delete-btn">
                          <IoTrashOutline className="inline-icon" /> Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {editingUser && (
            <div className="edit-user-form">
              <h3>Edit User</h3>
              <form onSubmit={(e) => { e.preventDefault(); handleSaveUser(); }}>
                <div className="form-group">
                  <label>Name:</label>
                  <input
                    type="text"
                    value={editingUser.name}
                    onChange={(e) => setEditingUser({ ...editingUser, name: e.target.value })}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Email:</label>
                  <input
                    type="email"
                    value={editingUser.email}
                    onChange={(e) => setEditingUser({ ...editingUser, email: e.target.value })}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Role:</label>
                  <select
                    value={editingUser.role_id}
                    onChange={(e) => setEditingUser({ ...editingUser, role_id: parseInt(e.target.value, 10) })}
                  >
                    <option value={1}>Admin</option>
                    <option value={2}>Home Viewer</option>
                    <option value={3}>Contestant</option>
                  </select>
                </div>
                <button type="submit" className="save-btn">Save Changes</button>
                <button onClick={() => setEditingUser(null)} className="cancel-btn">Cancel</button>
              </form>
            </div>
          )}
          {showDeleteConfirm && (
            <div className="delete-confirm-modal">
              <h3>Confirm Deletion</h3>
              <p>Are you sure you want to delete this user?</p>
              <button onClick={() => handleDeleteUser(showDeleteConfirm)} className="confirm-btn">Yes, Delete</button>
              <button onClick={() => setShowDeleteConfirm(null)} className="cancel-btn">Cancel</button>
            </div>
          )}
        </div>
      )}

      {activeTab === 'questions' && (
        <div className="tab-content">
          <div className="tab-header">
            <h2><IoChatbubbleEllipsesOutline className="inline-icon" /> Manage Questions</h2>
            <div className="search-bar">
              <IoSearchOutline className="inline-icon" />
              <input
                type="text"
                placeholder="Search questions..."
                value={questionSearchTerm}
                onChange={(e) => setQuestionSearchTerm(e.target.value)}
              />
              {questionSearchTerm && (
                <button onClick={clearQuestionSearch} className="clear-search">×</button>
              )}
            </div>
          </div>

          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Question Text</th>
                  <th>Session Date</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredQuestions.map(question => (
                  <tr key={question.id}>
                    <td>{question.id}</td>
                    <td>{question.question_text}</td>
                    <td>{question.session_date ? formatDate(question.session_date) : 'N/A'}</td>
                    <td>
                      {question.shown_on_air ? (
                        <span className="status-badge on-air">
                          <IoTvOutline className="inline-icon" /> On Air
                        </span>
                      ) : null}
                      {question.shown_to_home ? (
                        <span className="status-badge to-home">
                          <IoHomeOutline className="inline-icon" /> To Home
                        </span>
                      ) : null}
                      {!question.shown_on_air && !question.shown_to_home ? (
                        <span className="status-badge draft">
                          <IoDocumentTextOutline className="inline-icon" /> Draft
                        </span>
                      ) : null}
                    </td>
                    <td>
                      <div className="action-buttons">
                        <button onClick={() => handleEditUser(question)} className="edit-btn">
                          <IoCreateOutline className="inline-icon" /> Edit
                        </button>
                        <button onClick={() => setShowDeleteConfirm(question.id)} className="delete-btn">
                          <IoTrashOutline className="inline-icon" /> Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {editingUser && (
            <div className="edit-user-form">
              <h3>Edit Question</h3>
              <form onSubmit={(e) => { e.preventDefault(); handleSaveUser(); }}>
                <div className="form-group">
                  <label>Question Text:</label>
                  <input
                    type="text"
                    value={editingUser.question_text}
                    onChange={(e) => setEditingUser({ ...editingUser, question_text: e.target.value })}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Session Date:</label>
                  <input
                    type="date"
                    value={editingUser.session_date ? editingUser.session_date : ''}
                    onChange={(e) => setEditingUser({ ...editingUser, session_date: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>Status:</label>
                  <select
                    value={editingUser.shown_on_air ? 1 : (editingUser.shown_to_home ? 2 : 0)}
                    onChange={(e) => {
                      const status = parseInt(e.target.value, 10);
                      setEditingUser({
                        ...editingUser,
                        shown_on_air: status === 1,
                        shown_to_home: status === 2,
                        shown_to_home: status === 2,
                      });
                    }}
                  >
                    <option value={0}>Draft</option>
                    <option value={1}>On Air</option>
                    <option value={2}>To Home</option>
                  </select>
                </div>
                <button type="submit" className="save-btn">Save Changes</button>
                <button onClick={() => setEditingUser(null)} className="cancel-btn">Cancel</button>
              </form>
            </div>
          )}
          {showDeleteConfirm && (
            <div className="delete-confirm-modal">
              <h3>Confirm Deletion</h3>
              <p>Are you sure you want to delete this question?</p>
              <button onClick={() => handleDeleteQuestion(showDeleteConfirm)} className="confirm-btn">Yes, Delete</button>
              <button onClick={() => setShowDeleteConfirm(null)} className="cancel-btn">Cancel</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default AdminPanel;